import React, { useEffect, useState } from "react";
import "react-phone-number-input/style.css";
import PhoneNumberInput, {
  PhoneInputProps,
  isValidPhoneNumber,
} from "react-phone-number-input";

import { styled } from "../../../styles/theme";
import Icon from "../../../icons/Icon";

import { Text } from "../Text";

const Container = styled.div`
  width: 100%;

  .errorText {
    display: block;
    margin-top: ${(p) => p.theme.spacing.xs};

    &::first-letter {
      text-transform: capitalize;
    }
  }
`;

const Label = styled.label`
  display: block;
  line-height: 19px;
  margin-bottom: ${(p) => p.theme.spacing.xs};
`;

const ContainerPhoneNumberInput = styled(PhoneNumberInput)`
  display: flex;
  align-items: stretch;

  .PhoneInputCountry {
    display: flex;
    align-items: center;
    margin-right: ${(p) => p.theme.spacing.xs};

    .PhoneInputCountryIcon {
      width: 45px;
      height: 32px;
      box-shadow: none;
    }

    svg {
      margin-left: ${(p) => p.theme.spacing.xs};
    }

    .PhoneInputCountrySelectArrow {
      width: 6px;
      height: 6px;
      border-color: ${(p) => p.theme.colors.red["500"]};
    }
  }

  input {
    background: ${(p) =>
      p.disabled ? p.theme.colors.neutral[300] : "transparent"};
    border-radius: ${(p) => p.theme.radius};
    border: 1px solid;
    border-color: ${(p) =>
      p.error ? p.theme.colors.red["400"] : p.theme.colors.neutral[400]};
    padding: ${(p) => `${p.theme.spacing.sm} ${p.theme.spacing.md}`};

    width: 100%;
    min-height: 19px;

    font-family: ${(p) => p.theme.fonts.main};
    font-size: 14px;
    line-height: 19px;
    color: ${(p) =>
      p.disabled
        ? p.theme.colors.primary["200"]
        : p.theme.colors.primary["500"]};
  }
`;

export interface Props extends PhoneInputProps {
  label?: string;
  errors?: string[];
}

const PhoneInput: React.FC<Props> = ({ label, errors, ...props }) => {
  const [isFocused, setIsFocused] = useState(false);
  const [phoneError, setPhoneError] = useState("");

  useEffect(() => {
    if (
      props.value.length > 0 &&
      !isValidPhoneNumber(props.value) &&
      !isFocused
    ) {
      setPhoneError("your phone number seems to be invalid");
    } else if (phoneError) {
      setPhoneError("");
    }
  }, [props.value, isFocused]);

  return (
    <Container className="form__input">
      {label && (
        <Label htmlFor={props.id}>
          <Text weight={600}>{label}</Text>
        </Label>
      )}

      <ContainerPhoneNumberInput
        defaultCountry="BR"
        countrySelectProps={{
          arrowComponent: () => (
            <Icon name="chevron-down" size={16} fill="neutral.500" />
          ),
        }}
        placeholder="(DDD) XXXXX-XXXX"
        error={!isFocused ? phoneError || errors?.[0] : undefined}
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
        onErrorCapture={() => null}
        {...props}
      />

      {!isFocused && (phoneError !== "" || (errors && errors.length > 0)) && (
        <Text
          className="errorText"
          size={12}
          color="red"
          colorWeight="700"
          align="left"
        >
          {phoneError || errors?.[0]}.
        </Text>
      )}
    </Container>
  );
};

export default PhoneInput;
